import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  IconButton, 
  Typography, 
  Menu, 
  Container, 
  Avatar, 
  Button, 
  Tooltip, 
  MenuItem,
  Badge,
  Chip,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PollIcon from '@mui/icons-material/Poll';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import ExtensionIcon from '@mui/icons-material/Extension';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import HistoryIcon from '@mui/icons-material/History';
import GroupIcon from '@mui/icons-material/Group';
import ChatIcon from '@mui/icons-material/Chat';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../../resources/thinklinklogo.png';

export default function MainHeader() {
  const navigate = useNavigate();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  const [user, setUser] = useState(null);
  const [hasAssigned, setHasAssigned] = useState(false);
  const [respBalance, setRespBalance] = useState({ money: 0, points: 0 });
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Helper function to get full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    const baseUrl = API_BASE.replace('/api', '');
    return `${baseUrl}${avatarPath}`;
  };

  // Пункт «Назначенные опросы» — только если у заказчика есть назначения интервьюера
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user || user.role !== 'client' || !token) { setHasAssigned(false); return undefined; }
    let cancelled = false;
    axios.get(`${API_BASE}/surveys/assigned/has`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!cancelled) setHasAssigned(!!(res.data && res.data.has)); })
      .catch(() => { if (!cancelled) setHasAssigned(false); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role, API_BASE]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Функция для загрузки пользователя из localStorage
    const loadUser = () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        try {
          const parsedUser = JSON.parse(userStr);
          setUser(parsedUser);
        } catch (e) {
          console.error('Failed to parse user:', e);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    // Загружаем пользователя при монтировании
    loadUser();

    // Загрузка баланса респондента для AppBar
    const fetchRespondentBalance = async () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (!token || !userStr) return;
      try {
        const parsed = JSON.parse(userStr);
        if (parsed.role !== 'respondent') return;
        const res = await axios.get(`${API_BASE}/rewards/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRespBalance({ money: res.data?.money || 0, points: res.data?.points || 0 });
      } catch (e) {
        // не критично
      }
    };

    // Следим за изменением user и сразу обновляем баланс респондента
    // (например, после входа)
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const parsedUser = JSON.parse(userStr);
        if (parsedUser.role === 'respondent') {
          fetchRespondentBalance();
        }
      } catch (e) {}
    }

    // Слушаем кастомное событие authChange (для той же вкладки)
    const handleAuthChange = () => {
      loadUser();
      // Обновляем баланс респондента после изменения auth
      fetchRespondentBalance();
    };
    window.addEventListener('authChange', handleAuthChange);

    // Слушаем изменения в localStorage (для других вкладок)
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'token') {
        loadUser();
        fetchRespondentBalance();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Загрузка количества непрочитанных уведомлений
    const fetchCount = async () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (!token || !userStr) {
        setUnreadCount(0);
        setUnreadSupportCount(0);
        return;
      }
      try {
        const res = await axios.get(`${API_BASE}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const count = res.data.count || 0;
        console.log('MainHeader: Unread count:', count);
        setUnreadCount(count);

        // Fetch support unread count for respondents
        const parsed = JSON.parse(userStr);
        if (parsed.role === 'respondent' || parsed.role === 'client') {
          try {
            const supportRes = await axios.get(`${API_BASE}/support/unread-count`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setUnreadSupportCount(supportRes.data.count || 0);
          } catch (err) {
            console.error('Error fetching support unread count:', err);
          }
        } else if (parsed.role === 'admin') {
          // Fetch admin unread count
          try {
            const adminSupportRes = await axios.get(`${API_BASE}/support/admin/unread-count`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setUnreadSupportCount(adminSupportRes.data.count || 0);
          } catch (err) {
            console.error('Error fetching admin support unread count:', err);
          }
        }
      } catch (e) {
        console.error('MainHeader: Error fetching unread count:', e);
        setUnreadCount(0);
        setUnreadSupportCount(0);
      }
    };
    
    // Вызываем fetchCount только если пользователь авторизован
    let interval;
    if (localStorage.getItem('token')) {
      fetchCount();
      fetchRespondentBalance();
      interval = setInterval(() => {
        fetchCount();
        fetchRespondentBalance();
      }, 60000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      window.removeEventListener('authChange', handleAuthChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [API_BASE]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    // Удаляем данные из localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Вызываем событие authChange для обновления UI
    window.dispatchEvent(new Event('authChange'));
    // Перенаправляем на страницу входа
    navigate('/login');
  };

  // Динамические ссылки в зависимости от роли
  const getDashboardPath = () => {
    // Всегда проверяем актуальные данные из localStorage
    const userStr = localStorage.getItem('user');
    if (!userStr) return '/';
    
    try {
      const currentUser = JSON.parse(userStr);
      if (currentUser.role === 'admin') return '/admin/dashboard';
      if (currentUser.role === 'client') return '/client/dashboard';
      return '/respondent/dashboard';
    } catch (e) {
      console.error('Error parsing user in getDashboardPath:', e);
      return '/';
    }
  };

  const getProfilePath = () => {
    // Всегда проверяем актуальные данные из localStorage
    const userStr = localStorage.getItem('user');
    if (!userStr) return '/';
    
    try {
      const currentUser = JSON.parse(userStr);
      if (currentUser.role === 'admin') return '/admin/profile';
      if (currentUser.role === 'client') return '/client/profile';
      return '/respondent/profile';
    } catch (e) {
      console.error('Error parsing user in getProfilePath:', e);
      return '/';
    }
  };

  const getNotificationsPath = () => {
    // Всегда проверяем актуальные данные из localStorage
    const userStr = localStorage.getItem('user');
    if (!userStr) return '/';
    
    try {
      const currentUser = JSON.parse(userStr);
      if (currentUser.role === 'admin') return '/admin/notifications';
      if (currentUser.role === 'client') return '/client/notifications';
      return '/respondent/notifications';
    } catch (e) {
      console.error('Error parsing user in getNotificationsPath:', e);
      return '/';
    }
  };

  // Навигационные пункты в зависимости от роли
  const getNavigationItems = () => {
    // Всегда проверяем актуальные данные из localStorage
    const userStr = localStorage.getItem('user');
    if (!userStr) return [];
    
    try {
      const currentUser = JSON.parse(userStr);
      
      if (currentUser.role === 'admin') {
        return [
          { label: 'Панель управления', path: '/admin/dashboard', icon: <DashboardIcon /> },
          { label: 'Модерация', path: '/admin/moderation', icon: <AdminPanelSettingsIcon /> },
          { label: 'Профильные опросы', path: '/admin/profile-surveys', icon: <PollIcon /> },
          { 
            label: 'Чаты поддержки', 
            path: '/admin/support-chats', 
            icon: unreadSupportCount > 0 ? (
              <Badge badgeContent={unreadSupportCount} color="error">
                <ChatIcon />
              </Badge>
            ) : <ChatIcon />
          },
          { label: 'Создать тестовый аккаунт', path: '/admin/create-test-account', icon: <PersonAddIcon /> }
        ];
      }
      
      if (currentUser.role === 'client') {
        return [
          { label: 'Панель управления', path: '/client/dashboard', icon: <DashboardIcon /> },
          { label: 'Создать опрос', path: '/client/survey-builder', icon: <PollIcon /> },
          { 
            label: 'Поддержка', 
            path: '/client/support', 
            icon: unreadSupportCount > 0 ? (
              <Badge badgeContent={unreadSupportCount} color="error">
                <ChatIcon />
              </Badge>
            ) : <ChatIcon />
          }
        ];
      }
      
      // Respondent navigation - full menu in AppBar
      return [
        { label: 'Главная', path: '/respondent/dashboard', icon: <DashboardIcon /> },
        { label: 'История', path: '/respondent/history', icon: <HistoryIcon /> },
        { label: 'Баланс', path: '/respondent/balance', icon: <PersonIcon /> },
        { label: 'Рефералы', path: '/respondent/referrals', icon: <GroupIcon /> },
        { label: 'Подарки за баллы', path: '/respondent/rewards', icon: <CardGiftcardIcon /> },
        { 
          label: 'Поддержка', 
          path: '/respondent/support', 
          icon: unreadSupportCount > 0 ? (
            <Badge badgeContent={unreadSupportCount} color="error">
              <ChatIcon />
            </Badge>
          ) : <ChatIcon />
        }
      ];
    } catch (e) {
      console.error('Error parsing user in getNavigationItems:', e);
      return [];
    }
  };

  const navigationItems = getNavigationItems();
  
  const getRoleLabel = () => {
    if (!user) return '';
    if (user.role === 'admin') return 'Администратор';
    if (user.role === 'client') return 'Заказчик';
    return 'Респондент';
  };
  
  const getRoleColor = () => {
    if (!user) return 'default';
    if (user.role === 'admin') return 'error';
    if (user.role === 'client') return 'primary';
    return 'success';
  };

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        mb: 2,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ py: 1 }}>
          {/* Logo - Desktop (navigate to role-specific dashboard) */}
          <Box
            component={RouterLink}
            to={getDashboardPath()}
            onClick={(e) => {
              // Ensure navigation uses dynamic path even if user just loaded
              e.preventDefault();
              navigate(getDashboardPath());
            }}
            sx={{
              mr: 3,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              textDecoration: 'none',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              }
            }}
          >
            <img 
              src={logo} 
              alt="ThinkLink" 
              style={{ 
                height: '35px',
                filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))'
              }} 
            />
          </Box>

          {/* Mobile Menu Button */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{
                color: '#1976d2',
                background: 'rgba(25, 118, 210, 0.08)',
                borderRadius: '16px',
                padding: '10px',
                '&:hover': {
                  background: 'rgba(25, 118, 210, 0.15)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiPaper-root': {
                  borderRadius: '16px',
                  mt: 1,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                }
              }}
            >
              {navigationItems.map((item) => (
                <MenuItem
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  onClick={handleCloseNavMenu}
                  sx={{
                    borderRadius: '12px',
                    mx: 1,
                    my: 0.5,
                    '&:hover': {
                      background: 'rgba(116, 185, 255, 0.08)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {React.cloneElement(item.icon, { sx: { color: '#1976d2' } })}
                    <Typography sx={{ color: '#2d3748' }}>{item.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Logo - Mobile (navigate to role-specific dashboard) */}
          <Box
            component={RouterLink}
            to={getDashboardPath()}
            onClick={(e) => {
              e.preventDefault();
              navigate(getDashboardPath());
            }}
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
            }}
          >
            <img 
              src={logo} 
              alt="ThinkLink" 
              style={{ 
                height: '30px',
                filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))'
              }} 
            />
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {navigationItems.map((item) => (
              <Button
                key={item.label}
                component={RouterLink}
                to={item.path}
                startIcon={React.cloneElement(item.icon, { sx: { color: '#1976d2' } })}
                sx={{
                  color: '#2d3748',
                  fontWeight: 600,
                  borderRadius: '16px',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(116, 185, 255, 0.08)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Right Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {user && user.role === 'respondent' && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${(respBalance.money || 0).toFixed(2)} BYN`}
                  size="small"
                  component={RouterLink}
                  to="/respondent/balance"
                  clickable
                  sx={{
                    background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    borderRadius: '16px',
                    border: '1px solid rgba(116, 185, 255, 0.3)',
                    boxShadow: '0 4px 12px rgba(9, 132, 227, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0984e3 0%, #0652DD 100%)',
                      transform: 'scale(1.05)',
                      boxShadow: '0 6px 16px rgba(9, 132, 227, 0.4)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                />
                <Chip
                  label={`${(respBalance.points || 0).toFixed(1)} баллов`}
                  size="small"
                  component={RouterLink}
                  to="/respondent/balance"
                  clickable
                  sx={{
                    background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                    color: '#744210',
                    fontWeight: 600,
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 234, 167, 0.3)',
                    boxShadow: '0 4px 12px rgba(253, 203, 110, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)',
                      transform: 'scale(1.05)',
                      boxShadow: '0 6px 16px rgba(253, 203, 110, 0.4)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                />
              </Box>
            )}
            {user && user.role !== 'respondent' && (
              <Chip
                label={getRoleLabel()}
                color={getRoleColor()}
                size="small"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  fontWeight: 600,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              />
            )}
            {user && (
              <IconButton
                color="primary"
                component={RouterLink}
                to={getNotificationsPath()}
                aria-label="уведомления"
                sx={{
                  background: 'rgba(25, 118, 210, 0.08)',
                  borderRadius: '12px',
                  '&:hover': {
                    background: 'rgba(25, 118, 210, 0.15)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            {user ? (
              <Box sx={{ flexGrow: 0 }}>
                <Tooltip title="Настройки профиля">
                  <IconButton 
                    onClick={handleOpenUserMenu} 
                    sx={{ 
                      p: 0.5,
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Avatar 
                      alt={user.fullName || user.email}
                      src={getAvatarUrl(user.avatar)}
                      sx={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      }}
                    >
                      {(user.fullName || user.email || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ 
                    mt: '45px',
                    '& .MuiPaper-root': {
                      borderRadius: '16px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      minWidth: '220px',
                    }
                  }}
                  id="menu-user"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem disabled>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#111', fontSize: '1.05rem' }}>
                        {user.fullName || 'Пользователь'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#222', fontWeight: 500, fontSize: '0.98rem' }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      handleCloseUserMenu();
                      navigate(getProfilePath());
                    }}
                    sx={{
                      borderRadius: '12px',
                      mx: 1,
                      my: 0.5,
                      '&:hover': {
                        background: 'rgba(116, 185, 255, 0.08)',
                      }
                    }}
                  >
                    <PersonIcon sx={{ mr: 1, color: '#1976d2' }} fontSize="small" />
                    <Typography sx={{ color: '#2d3748' }}>Профиль</Typography>
                  </MenuItem>
                  {user?.role === 'client' && hasAssigned && (
                    <MenuItem
                      onClick={() => {
                        handleCloseUserMenu();
                        navigate('/client/assigned');
                      }}
                      sx={{
                        borderRadius: '12px',
                        mx: 1,
                        my: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.08)',
                        }
                      }}
                    >
                      <ExtensionIcon sx={{ mr: 1, color: '#1976d2' }} fontSize="small" />
                      <Typography sx={{ color: '#2d3748' }}>Назначенные опросы</Typography>
                    </MenuItem>
                  )}
                  {user?.role === 'client' && (
                    <MenuItem
                      onClick={() => {
                        handleCloseUserMenu();
                        navigate('/client/integrations');
                      }}
                      sx={{
                        borderRadius: '12px',
                        mx: 1,
                        my: 0.5,
                        '&:hover': {
                          background: 'rgba(116, 185, 255, 0.08)',
                        }
                      }}
                    >
                      <ExtensionIcon sx={{ mr: 1, color: '#1976d2' }} fontSize="small" />
                      <Typography sx={{ color: '#2d3748' }}>Интеграции</Typography>
                    </MenuItem>
                  )}
                  {/* Кнопка Панель управления удалена для всех типов аккаунтов */}
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      handleCloseUserMenu();
                      handleLogout();
                    }}
                    sx={{
                      borderRadius: '12px',
                      mx: 1,
                      my: 0.5,
                      color: '#d32f2f',
                      '&:hover': {
                        background: 'rgba(211, 47, 47, 0.08)',
                      }
                    }}
                  >
                    <ExitToAppIcon sx={{ mr: 1 }} fontSize="small" />
                    <Typography>Выйти</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Button
                component={RouterLink}
                to="/login"
                sx={{
                  color: '#1976d2',
                  fontWeight: 600,
                  borderRadius: '16px',
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  border: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Войти
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}